import { createContext, useContext, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check } from "lucide-react";
import axios from "axios";
import { API_URL } from "../data/content";
import { EASE, SolidButton } from "./Shared";

const Ctx = createContext({ open: () => {} });
export const useConsultation = () => useContext(Ctx);

const SERVICES = ["Women", "Men", "Kids", "Bridal"];

const Field = ({ label, children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block">
    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-fog">{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full border border-line/15 bg-bone/5 px-4 py-3 text-sm text-bone placeholder:text-fog/60 focus:border-flame focus:outline-none transition-colors duration-300";

export const ConsultationProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "Women", date: "", message: "" });

  const open = () => {
    setSent(false);
    setError("");
    setIsOpen(true);
  };
  const close = () => setIsOpen(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await axios.post(`${API_URL}/consultations`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        service: form.service,
        preferred_date: form.date,
        message: form.message,
      });
      setSent(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Ctx.Provider value={{ open }}>
      {children}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              key="backdrop"
              className="fixed inset-0 z-[95] bg-ink/70 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={close}
              data-testid="consultation-backdrop"
            />
            <motion.aside
              key="drawer"
              className="fixed right-0 top-0 z-[100] flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-line/10 bg-coal p-8 sm:p-10"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.55, ease: EASE }}
              data-testid="consultation-drawer"
            >
              <div className="mb-10 flex items-start justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-flame">Bespoke Consultation</p>
                  <h3 className="mt-3 font-serif text-3xl font-light">Made for you.</h3>
                </div>
                <button
                  type="button"
                  onClick={close}
                  data-testid="consultation-close-button"
                  className="border border-line/15 p-2 text-fog transition-colors duration-300 hover:border-flame hover:text-flame"
                  aria-label="Close consultation form"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {sent ? (
                <div className="flex flex-1 flex-col items-start justify-center" data-testid="consultation-success">
                  <span className="mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-flame">
                    <Check className="h-6 w-6 text-flame" />
                  </span>
                  <h4 className="font-serif text-3xl font-light">Request received.</h4>
                  <p className="mt-4 max-w-xs text-sm font-light leading-relaxed text-fog">
                    Our atelier will contact you shortly to begin the conversation.
                  </p>
                  <div className="mt-8">
                    <SolidButton onClick={close} testId="consultation-done-button">Continue</SolidButton>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="flex flex-1 flex-col gap-6" data-testid="consultation-form">
                  <Field label="Your Name" htmlFor="c-name">
                    <input id="c-name" required data-testid="consultation-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Full name" />
                  </Field>
                  <Field label="Contact Number" htmlFor="c-phone">
                    <input id="c-phone" required data-testid="consultation-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91" />
                  </Field>
                  <Field label="Email" htmlFor="c-email">
                    <input id="c-email" type="email" required data-testid="consultation-email-input" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                  </Field>
                  <Field label="Service" htmlFor="c-service">
                    <div className="grid grid-cols-4 gap-2" data-testid="consultation-service-group">
                      {SERVICES.map((s) => (
                        <button
                          key={s}
                          type="button"
                          data-testid={`consultation-service-${s.toLowerCase()}`}
                          onClick={() => setForm((f) => ({ ...f, service: s }))}
                          className={`border px-2 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                            form.service === s ? "border-flame bg-flame text-ink" : "border-line/15 text-fog hover:border-flame/60 hover:text-bone"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Preferred Date" htmlFor="c-date">
                    <input id="c-date" type="date" data-testid="consultation-date-input" className={`${inputCls} [color-scheme:dark]`} value={form.date} onChange={set("date")} />
                  </Field>
                  <Field label="Your Message (optional)" htmlFor="c-message">
                    <textarea id="c-message" rows={3} data-testid="consultation-message-input" className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about the occasion, the silhouette you imagine..." />
                  </Field>
                  {error && <p className="font-mono text-xs text-flame" data-testid="consultation-error">{error}</p>}
                  <div className="mt-auto pt-4">
                    <SolidButton type="submit" testId="consultation-submit-button">
                      {sending ? "Sending..." : "Request Appointment"}
                    </SolidButton>
                    <p className="mt-4 font-mono text-[10px] tracking-wide text-fog">Our staff will contact you as soon as possible.</p>
                  </div>
                </form>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </Ctx.Provider>
  );
};
